/*
 * API client for the partner surface of Backend-v2 (docs/API.md, section 1).
 *
 * Same design as the client app's src/api/client.ts. The session lives in
 * httpOnly cookies (wop_access, wop_refresh) the browser sets and sends on its
 * own, so this file never sees a token, and neither does any other script on
 * the page: an XSS bug or a compromised dependency cannot walk off with a
 * partner's session, and with it their payout bank account.
 *
 * Because the cookies travel automatically, writes need a CSRF defence. The
 * server also sets a READABLE cookie (wop_csrf); we echo it back in
 * X-CSRF-Token. An attacker's origin can cause the browser to send the session
 * cookie, but cannot read wop_csrf to echo it.
 *
 * Money is a decimal string with 2 dp ("1000.00") everywhere in this file. It
 * is only turned into a number at the edge of the UI (src/ui/format.ts).
 */

const BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

const CSRF_COOKIE = "wop_csrf";

/** The readable half of the double-submit CSRF pair. */
function csrfToken(): string | null {
  const hit = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${CSRF_COOKIE}=`));
  return hit ? decodeURIComponent(hit.slice(CSRF_COOKIE.length + 1)) : null;
}

/*
 * The transport. Normally the browser's fetch; with VITE_MOCK=1 (dev only) the
 * sample-data server in ./mock.ts answers instead, with real Response objects,
 * so every path below (CSRF, refresh, error details) runs exactly as it would
 * against Backend-v2. Vite inlines import.meta.env.VITE_MOCK at build time, so
 * in a normal build the condition is constant-false and the dynamic import of
 * ./mock is dropped from the bundle entirely.
 */
let mockFetch: Promise<(url: string, init: RequestInit) => Promise<Response>> | null = null;

function transport(url: string, init: RequestInit): Promise<Response> {
  if (import.meta.env.VITE_MOCK === "1") {
    mockFetch ??= import("./mock").then((m) => m.mockFetch);
    return mockFetch.then((f) => f(url, init));
  }
  return fetch(url, init);
}

type Options = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

/** Fired when the session is gone for good; AuthContext returns to Login.
 * A CustomEvent whose detail is a SessionExpiredDetail. */
export const SESSION_EXPIRED = "wop:session-expired";

/** `message`: why the session ended, when the server said so (a suspended or
 * closed partner account). Shown on the sign-in screen. */
export type SessionExpiredDetail = { message: string | null };

/* The access and CSRF cookies last 15 minutes; the refresh cookie lasts 30
 * days. When a call comes back 401 (or the CSRF cookie has lapsed), rotate the
 * session once and retry. Concurrent calls share one refresh: the server
 * rotates the refresh token, so a second refresh racing the first would fail
 * and sign the partner out. */
type RefreshResult = { ok: boolean; status: number; message: string | null };

let refreshing: Promise<RefreshResult> | null = null;

function refreshSession(): Promise<RefreshResult> {
  refreshing ??= transport(`${BASE}/partner/auth/refresh`, {
    method: "POST",
    headers: { Accept: "application/json", "X-Client": "web" },
    credentials: "include",
  })
    .then(async (r) => ({ ok: r.ok, status: r.status, message: r.ok ? null : accountEnded(await detailOf(r)) }))
    .catch(() => ({ ok: false, status: 0, message: null }))
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/** Auth routes answer 401 as a result, not as an expired session. */
const NO_RETRY = /^\/partner\/auth\/(login|refresh|logout|request-code|verify-code)$/;

/**
 * Ops suspended or closed the partner: every call now answers
 * 403 "This partner account is suspended." (or "closed"). That is the end of
 * the session, not an error for the page to show. A pending partner's 403
 * ("awaiting approval") is NOT this: it is a normal error on the one action.
 */
function accountEnded(detail: unknown): string | null {
  return typeof detail === "string" && /partner account is (suspended|closed)/i.test(detail) ? detail : null;
}

async function detailOf(res: Response): Promise<unknown> {
  try {
    return (await res.clone().json())?.detail;
  } catch {
    return undefined;
  }
}

function send(path: string, method: string, opts: Options): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    // Tells the server this is the browser client, so it replies with cookies
    // rather than handing tokens back in the body.
    "X-Client": "web",
  };

  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  if (method !== "GET" && method !== "HEAD") {
    const token = csrfToken();
    if (token) headers["X-CSRF-Token"] = token;
  }

  return transport(`${BASE}${path}`, {
    method,
    headers,
    // Without this the session cookies are not sent at all.
    credentials: "include",
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
}

function endSession(message: string | null) {
  const detail: SessionExpiredDetail = { message };
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED, { detail }));
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";

  let res = await send(path, method, opts);

  const expired = (r: Response) =>
    r.status === 401 ||
    (r.status === 403 && method !== "GET" && method !== "HEAD" && !csrfToken());
  if (expired(res) && !NO_RETRY.test(path)) {
    const refreshed = await refreshSession();
    // 409: another tab of this browser refreshed first, and its response set
    // fresh cookies. The retry picks them up.
    if (refreshed.ok || refreshed.status === 409) res = await send(path, method, opts);
    if (expired(res)) endSession(refreshed.message);
  }

  if (res.status === 204) return undefined as T;

  let payload: any = null;
  try {
    payload = await res.json();
  } catch {
    /* empty or non-JSON body */
  }

  if (!res.ok) {
    // FastAPI puts validation errors in an array; surface the first one
    // rather than "[object Object]".
    const detail = payload?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? String(detail[0].msg)
          : `Request failed (${res.status})`;
    if (res.status === 403 && !NO_RETRY.test(path)) {
      const ended = accountEnded(detail);
      if (ended) endSession(ended);
    }
    throw new ApiError(res.status, message, detail);
  }

  return payload as T;
}

/** A message safe to show for anything api() can throw. */
export function errorMessage(err: unknown, fallback = "Something went wrong. Please try again."): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof TypeError) return "Wealthoak could not be reached. Check your connection and try again.";
  return fallback;
}

/* ---- types ---------------------------------------------------------------- */

/** Decimal string, 2 dp: "1000.00". */
export type Money = string;
/** ISO-8601 timestamp with offset. */
export type Timestamp = string;
/** YYYY-MM-DD. */
export type DateOnly = string;

export type PartnerStatus = "pending" | "active" | "suspended" | "closed";

export type PartnerMe = {
  id: string;
  code: string;
  full_name: string;
  email: string;
  phone: string | null;
  company_name: string | null;
  city: string | null;
  status: PartnerStatus;
  /**
   * Percent as a decimal string, "10.00" = 10 %; null = not set yet. On a
   * tiered programme this is the rate of the slab the partner is in now.
   */
  commission_rate: string | null;
  /** The tiered commission programme; null for a partner on a flat rate. */
  commission_program: CommissionProgram | null;
  has_password: boolean;
  hold_days: number;
  joined_at: Timestamp;
  /** Client onboarding with ?ref=<code>; what the partner shares. */
  referral_link: string;
  /**
   * "Add user" credits: +1 each time a client who came through the referral
   * link pays their first subscription. Spent by POST /partner/added-users.
   */
  user_credits: UserCredits;
};

/** Clients from `from` to `to` (both inclusive; null = no upper end) earn `rate` percent. */
export type CommissionSlab = { from: number; to: number | null; rate: string };

/**
 * Tiered commission (Tier 1). The partner's rate depends on how many clients
 * they have; the first `activation_clients` clients earn nothing payable yet
 * (their commission is recorded as "in progress") and payout starts with
 * client number `activation_clients + 1`.
 */
export type CommissionProgram = {
  tier: number;
  /** Referred clients who have chosen a plan, so far. */
  client_count: number;
  activation_clients: number;
  /** In client order, e.g. 0-25 at 10 %, 26-99 at 15 %, 100+ at 20 %. */
  slabs: CommissionSlab[];
};

export type UserCredits = {
  earned: number;
  /** Spent on a request Wealthoak approved. */
  used: number;
  /** Spent on a request Wealthoak hasn't decided yet. */
  pending: number;
  available: number;
};

export type LoginOut = {
  csrf_token: string;
  must_set_password: boolean;
  partner: PartnerMe;
};

export type PayoutAccountMasked = {
  account_holder: string;
  account_last4: string;
  ifsc: string;
  bank_name: string | null;
  upi_id: string | null;
  updated_at: Timestamp;
};

export type PayoutAccountInput = {
  account_holder: string;
  account_number: string;
  ifsc: string;
  bank_name?: string;
  upi_id?: string;
};

export type Wallet = {
  currency: "INR" | string;
  /** Withdrawable now. */
  available: Money;
  /** Earned, still inside the hold. */
  pending: Money;
  /** Requested or approved withdrawals, not yet paid. */
  in_process: Money;
  /** Paid to the bank, lifetime. */
  withdrawn: Money;
  /** Paid on the partner's own Wealthoak invoices from the wallet, lifetime. */
  set_off: Money;
  /** All accrued commission that is payable (pending + available); leaves out `in_progress`. */
  lifetime: Money;
  /** Commission recorded while the partner is below the activation count; not in the wallet yet. */
  in_progress: Money;
  hold_days: number;
  /**
   * Taken out of every withdrawal: the amount is GST-inclusive, TDS is on the
   * taxable part. Percent strings, e.g. "18.00". TDS is 20% without a PAN.
   */
  deductions: { gst_rate: string; tds_rate: string; pan_on_file: boolean };
  /** The earliest pending commission becoming available. */
  next_release: { date: string; amount: Money } | null;
  payout_account: PayoutAccountMasked | null;
};

export type ReferralStatus = "submitted" | "contacted" | "converted" | "rejected";

/** This is ALL a partner sees about a client: no portfolio, no holdings. */
export type ClientSummary = {
  referral_id: string;
  client_name: string;
  account_status: string;
  plan_code: string | null;
  plan_name: string | null;
  subscription_status: string | null;
  subscribed_until: Timestamp | null;
  client_since: Timestamp;
  /** Accrued and payable (not reversed); leaves out `commission_in_progress`. */
  commission_total: Money;
  /** Recorded for this client while the partner is below the activation count. */
  commission_in_progress: Money;
};

export type Referral = {
  id: string;
  lead_name: string;
  lead_email: string;
  lead_phone: string | null;
  notes: string | null;
  status: ReferralStatus;
  /** partner = typed in here; staff = attached by Wealthoak; link = came through the referral link; credit = an add-user request. */
  source?: ReferralSource;
  /** Shown when rejected. */
  status_reason: string | null;
  created_at: Timestamp;
  converted_at: Timestamp | null;
  /** Only when converted. */
  client: ClientSummary | null;
  /** Lifetime, accrued (not reversed). */
  commission_total: Money;
};

export type ReferralSource = "partner" | "staff" | "link" | "credit";

export type AddUserInput = { name: string; email: string; phone?: string; notes?: string };

export type ReferralInput = {
  lead_name: string;
  lead_email: string;
  lead_phone?: string;
  notes?: string;
};

/** in_progress: recorded, but the partner is still below the activation count, so it is not payable yet. */
export type CommissionState = "in_progress" | "pending" | "available" | "reversed";

export type Commission = {
  id: string;
  /** The client's referral id (matches ClientSummary.referral_id). Not sent by the API yet; the client sheet falls back to the name. */
  referral_id?: string;
  client_name: string | null;
  /** The plan the client paid for. */
  plan_name: string | null;
  invoice_number: string | null;
  /** Incl. GST. */
  payment_amount: Money;
  /** Excl. GST: what the rate applies to. */
  base_amount: Money;
  rate: string;
  amount: Money;
  earned_at: Timestamp;
  available_at: Timestamp;
  state: CommissionState;
  reversed_at: Timestamp | null;
  reversed_reason: string | null;
};

export type CommissionTotals = {
  in_progress: Money;
  pending: Money;
  available: Money;
  reversed: Money;
  total: Money;
};

export type Dashboard = {
  wallet: Wallet;
  referrals: Record<ReferralStatus, number> & { total: number };
  clients: { total: number; subscribed: number };
  /** Last 12 months, oldest first, zero-filled. month = "YYYY-MM". */
  earnings_by_month: { month: string; amount: Money }[];
  /** Up to 5. */
  recent_commissions: Commission[];
};

export type WithdrawalStatus = "requested" | "approved" | "paid" | "rejected" | "cancelled";

/**
 * 'bank': a withdrawal to the partner's bank account. 'subscription': a
 * set-off, the partner paid an invoice on their own Wealthoak subscription
 * from the wallet (it goes to Zoho at once and can't be cancelled).
 */
export type WithdrawalMethod = "bank" | "subscription";

export type Withdrawal = {
  id: string;
  method: WithdrawalMethod;
  /** The partner's own invoice a set-off paid; null for a bank withdrawal. */
  invoice_number: string | null;
  /** Gross: what left the wallet. GST-inclusive. */
  amount: Money;
  taxable_amount: Money;
  gst_rate: string;
  gst_amount: Money;
  tds_rate: string;
  tds_amount: Money;
  /** amount - GST - TDS: what is paid into the bank, or on the invoice for a set-off. */
  net_amount: Money;
  currency: string;
  status: WithdrawalStatus;
  requested_at: Timestamp;
  decided_at: Timestamp | null;
  paid_at: Timestamp | null;
  /** Set when the partner cancels it. */
  cancelled_at: Timestamp | null;
  /** The bank's UTR once paid; for a set-off, the Zoho payment number once applied. */
  payout_reference: string | null;
  reject_reason: string | null;
  /** The bank account as it was when the withdrawal was requested; null for a set-off. */
  account: { account_holder: string; account_last4: string; ifsc: string; bank_name: string | null } | null;
  /**
   * Who started it: "partner" pressed Withdraw; "auto" is the monthly auto
   * payout, which skips finance approval and goes out through RazorpayX.
   * Absent on older backends, which means "partner".
   */
  source?: "partner" | "auto";
};

/**
 * The monthly auto payout. When it is on, on `payout_day` of each month the
 * backend withdraws everything available in the wallet to the partner's bank
 * account through RazorpayX, with GST and TDS taken off as for a withdrawal.
 */
export type AutoPayout = {
  enabled: boolean;
  /** Day of the month (1 to 28) the payout runs. Set by Wealthoak, not the partner. */
  payout_day: number;
  /** An available balance below this waits for the next month. */
  min_amount: Money;
  /** The next run, in India; null while it is off. */
  next_run: DateOnly | null;
  /** The latest auto payout, whatever its outcome; null if none has run yet. */
  last_run: { date: DateOnly; amount: Money; status: WithdrawalStatus } | null;
};

/** An unpaid or part-paid invoice on the partner's own subscription, read live from Zoho Billing. */
export type OwnInvoice = {
  /** Zoho's invoice id, used in the pay URL. */
  id: string;
  number: string;
  date: DateOnly | null;
  due_date: DateOnly | null;
  /** Zoho's word for it: "sent", "overdue", "partially_paid", … */
  status: string;
  total: Money;
  /** Still to pay. */
  balance: Money;
  /** The most the wallet can put towards it now: what's available, or just enough to clear it after GST and TDS. */
  max_from_wallet: Money;
  /** A payment from the wallet on this invoice is still going through. */
  in_progress: boolean;
};

export type OwnInvoices = {
  /**
   * linked: the partner's own subscription was found; items are its unpaid invoices.
   * not_found: no Wealthoak subscription under the partner's email (Ops can link one).
   * unavailable: paying from the wallet isn't switched on yet.
   */
  status: "linked" | "not_found" | "unavailable";
  items: OwnInvoice[];
};

/**
 * commission: earned (credit) · commission_reversal: taken back after a refund (debit)
 * withdrawal: requested, gross amount (debit) · withdrawal_return: rejected or cancelled, back in the wallet (credit)
 */
export type TransactionKind = "commission" | "commission_reversal" | "withdrawal" | "withdrawal_return";

/** One movement of money through the wallet. */
export type Transaction = {
  /** "<kind>:<row id>": a commission and its reversal share a row. */
  id: string;
  kind: TransactionKind;
  direction: "credit" | "debit";
  amount: Money;
  /** The wallet balance (pending + available) right after this entry. */
  balance_after: Money;
  at: Timestamp;
  /** The row behind a commission or commission_reversal entry. */
  commission: Commission | null;
  /** The row behind a withdrawal or withdrawal_return entry. */
  withdrawal: Withdrawal | null;
};

/** The wallet as a statement for a range of days. */
export type Ledger = {
  /** The balance before the range starts. */
  opening_balance: Money;
  /** The balance at the end of the range. */
  closing_balance: Money;
  /** For the whole range, even when `items` is cut short. */
  totals: { credits: Money; debits: Money };
  /** Newest first, capped at 500. */
  items: Transaction[];
  /** True when the range has more entries than `items` holds. */
  truncated: boolean;
};

type Items<T> = { items: T[] };

/** "?from=…&to=…" for the date-filtered lists; "" when there is no range. */
function rangeQuery(range: { from?: DateOnly; to?: DateOnly }): string {
  const q = new URLSearchParams();
  if (range.from) q.set("from", range.from);
  if (range.to) q.set("to", range.to);
  const qs = q.toString();
  return qs ? `?${qs}` : "";
}

/* ---- endpoints ------------------------------------------------------------ */

export const auth = {
  requestCode: (email: string) =>
    api<{ status: string; message: string }>("/partner/auth/request-code", {
      method: "POST",
      body: { email },
    }),

  verifyCode: (email: string, code: string) =>
    api<LoginOut>("/partner/auth/verify-code", { method: "POST", body: { email, code } }),

  loginWithPassword: (email: string, password: string) =>
    api<LoginOut>("/partner/auth/login", { method: "POST", body: { email, password } }),

  /** Min 10 characters. Needs CSRF (sent automatically). */
  setPassword: (newPassword: string) =>
    api<void>("/partner/auth/set-password", { method: "POST", body: { new_password: newPassword } }),

  me: () => api<PartnerMe>("/partner/auth/me"),

  refresh: () => api<LoginOut>("/partner/auth/refresh", { method: "POST" }),

  logout: () => api<void>("/partner/auth/logout", { method: "POST" }),
};

/** "Become a partner" (POST /partner/apply). Public: no session, no CSRF. */
export type ApplicationInput = {
  full_name: string;
  email: string;
  phone: string;
  /** Permanent Account Number, upper-case: "ABCDE1234F". */
  pan: string;
  /** GST registration number, 15 characters, upper-case: "27ABCDE1234F1Z5". */
  gstin: string;
  city?: string;
  company_name?: string;
  /** How they would bring clients. Max 1000 characters. */
  message?: string;
  consent: boolean;
  /** Honeypot. The form hides it from people; it must stay empty. */
  website?: string;
};

/**
 * Same 202 for a new application and an email that is already a partner, so
 * the confirmation must not say the application was "created". 429 when the
 * network has applied too often.
 */
export const applyToPartner = (body: ApplicationInput) =>
  api<{ status: string; message: string }>("/partner/apply", { method: "POST", body });

/**
 * Profit share (display only): what the partner's clients invested in total,
 * the rate that puts them on, and the partner's cut of Wealthoak's profit
 * share from those clients. Nothing here moves money.
 */
export type ProfitShare = {
  /** Newest data behind the figures; null when there is no data at all. */
  as_of: string | null;
  /** Rupees: the partner's clients' broker account funds, added together. */
  investment: Money;
  /** Percent, e.g. "1.50". */
  rate_pct: string;
  /** The next rate up; null at the top rate. */
  next_tier: { rate_pct: string; above_investment: Money; remaining: Money } | null;
  users: number;
  users_sharing: number;
  company_profit: Money;
  partner_share: Money;
};

export const partner = {
  dashboard: () => api<Dashboard>("/partner/dashboard"),
  profitShare: () => api<ProfitShare>("/partner/profit-share"),
  updateProfile: (body: { phone?: string | null; company_name?: string | null; city?: string | null }) =>
    api<PartnerMe>("/partner/profile", { method: "PATCH", body }),
};

export const referrals = {
  list: (status?: ReferralStatus) =>
    api<Items<Referral>>(`/partner/referrals${status ? `?status=${status}` : ""}`),
  create: (body: ReferralInput) => api<Referral>("/partner/referrals", { method: "POST", body }),
};

/** Spend an add-user credit: Wealthoak confirms the person and adds them under you, or returns the credit. */
export const addedUsers = {
  create: (body: AddUserInput) => api<Referral>("/partner/added-users", { method: "POST", body }),
  cancel: (id: string) => api<Referral>(`/partner/added-users/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
};

export const clients = {
  list: () => api<Items<ClientSummary>>("/partner/clients"),
};

export const commissions = {
  list: (range: { from?: DateOnly; to?: DateOnly } = {}) =>
    api<Items<Commission> & { totals: CommissionTotals }>(`/partner/commissions${rangeQuery(range)}`),
};

/** The wallet as a statement: every credit and debit, with the balance after it. */
export const transactions = {
  list: (range: { from?: DateOnly; to?: DateOnly } = {}) => api<Ledger>(`/partner/transactions${rangeQuery(range)}`),
};

/**
 * A partner who is also a Wealthoak client pays an invoice on their own
 * subscription from the wallet. GST and TDS come off as for a withdrawal.
 */
export const ownInvoices = {
  list: () => api<OwnInvoices>("/partner/own-invoices"),
  /** amount: the gross taken from the wallet, "400.00". Returns the set-off (a Withdrawal). */
  payFromWallet: (invoiceId: string, amount: Money) =>
    api<Withdrawal>(`/partner/own-invoices/${encodeURIComponent(invoiceId)}/pay-from-wallet`, {
      method: "POST",
      body: { amount },
    }),
};

export const wallet = {
  get: () => api<Wallet>("/partner/wallet"),
  withdrawals: () => api<Items<Withdrawal>>("/partner/withdrawals"),
  /** amount: decimal string, "400.00". */
  withdraw: (amount: Money) => api<Withdrawal>("/partner/withdrawals", { method: "POST", body: { amount } }),
  cancel: (id: string) =>
    api<Withdrawal>(`/partner/withdrawals/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  payoutAccount: () => api<PayoutAccountMasked | null>("/partner/payout-account"),
  savePayoutAccount: (body: PayoutAccountInput) =>
    api<PayoutAccountMasked>("/partner/payout-account", { method: "PUT", body }),
  autoPayout: () => api<AutoPayout>("/partner/auto-payout"),
  setAutoPayout: (enabled: boolean) => api<AutoPayout>("/partner/auto-payout", { method: "PUT", body: { enabled } }),
};
